import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { LuInfo, LuArrowRight, LuStar, LuBookOpen, LuCircleCheck, LuCircleAlert } from 'react-icons/lu';

function InfoSn() {
  const { t } = useTranslation();
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 py-20 px-4">
      {/* Decorative Background Elements */}
      <div className="fixed inset-0 overflow-hidden -z-10">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-orange-200/20 to-yellow-200/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full blur-3xl"></div>
      </div>

      <motion.div 
        className="max-w-5xl mx-auto"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Header avec badge */}
        <motion.div 
          className="text-center mb-16"
          variants={itemVariants}
        >
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-orange-200/50 rounded-full px-4 py-2 mb-4 shadow-lg">
            <LuStar className="w-4 h-4 text-orange-500" />
            <span className="text-sm font-medium text-gray-700">{t('infoSn.badge')}</span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('infoSn.heading')}{" "}
            <span className="bg-gradient-to-r from-orange-500 to-yellow-500 bg-clip-text text-transparent">
              {t('infoSn.heading_highlight')}
            </span>
          </h1>

          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('infoSn.subtitle')}
          </p>
        </motion.div>

        {/* Section 1 */}
        <motion.div 
          className="bg-white rounded-[2rem] p-8 md:p-10 mb-10 shadow-xl border border-orange-100"
          variants={itemVariants}
        >
          <div className="flex items-start">
            <div className="hidden md:block w-1.5 h-16 bg-gradient-to-b from-orange-500 to-yellow-500 rounded-full mr-6 flex-shrink-0"></div>
            <div className="flex-1">
              <div className="flex items-center mb-6">
                <div className="p-2 bg-orange-100 rounded-xl mr-4">
                  <LuInfo className="w-6 h-6 text-orange-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">
                  {t('infoSn.section1_title')}
                </h2>
              </div>

              <p className="text-lg text-gray-700 leading-relaxed">
                {t('infoSn.section1_body')}
              </p>

              <div className="mt-6 p-6 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl border-l-4 border-orange-500">
                <p className="text-gray-700 font-medium">
                  {t('infoSn.section1_highlight')}
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Section 2 */}
        <motion.div 
          className="bg-white rounded-[2rem] p-8 md:p-10 mb-10 shadow-xl border border-orange-100"
          variants={itemVariants}
        >
          <div className="flex items-start">
            <div className="hidden md:block w-1.5 h-16 bg-gradient-to-b from-orange-500 to-yellow-500 rounded-full mr-6 flex-shrink-0"></div>
            <div className="flex-1">
              <div className="flex items-center mb-6">
                <div className="p-2 bg-orange-100 rounded-xl mr-4">
                  <LuBookOpen className="w-6 h-6 text-orange-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">
                  {t('infoSn.section2_title')}
                </h2>
              </div>

              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                {t('infoSn.section2_body')}
              </p>

              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                  <div className="p-1 bg-orange-100 rounded-lg mr-2">
                    <LuCircleCheck className="w-5 h-5 text-orange-600" />
                  </div>
                  {t('infoSn.elements_heading')}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Salaire de base */}
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-all">
                    <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                      <div className="w-2 h-8 bg-orange-500 rounded-full mr-3"></div>
                      {t('infoSn.element_base_title')}
                    </h4>
                    <p className="text-gray-700 leading-relaxed">
                      {t('infoSn.element_base_body')}
                    </p>
                  </div>

                  {/* Salaire brut */}
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-all">
                    <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                      <div className="w-2 h-8 bg-orange-500 rounded-full mr-3"></div>
                      {t('infoSn.element_gross_title')}
                    </h4>
                    <p className="text-gray-700 leading-relaxed">
                      {t('infoSn.element_gross_body')}
                    </p>
                  </div>

                  {/* Sursalaire */}
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-all">
                    <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                      <div className="w-2 h-8 bg-orange-500 rounded-full mr-3"></div>
                      {t('infoSn.element_additional_title')}
                    </h4>
                    <p className="text-gray-700 leading-relaxed">
                      {t('infoSn.element_additional_body')}
                    </p>
                  </div>

                  {/* Salaire net */}
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-all">
                    <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                      <div className="w-2 h-8 bg-orange-500 rounded-full mr-3"></div>
                      {t('infoSn.element_net_title')}
                    </h4>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      {t('infoSn.element_net_body')}
                    </p>
                    {/* <div className="bg-gradient-to-r from-orange-500 to-yellow-500 text-white rounded-xl p-4 shadow-sm">
                      <p className="font-semibold text-center">
                        SALAIRE NET = (SALAIRE BRUT – COTISATION SALARIALE) + INDEMNITE DE TRANSPORT
                      </p>
                    </div> */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Section différence brut/net */}
        <motion.div 
          className="bg-white rounded-[2rem] p-8 md:p-10 mb-10 shadow-xl border border-orange-100"
          variants={itemVariants}
        >
          <div className="flex items-start">
            <div className="hidden md:block w-1.5 h-16 bg-gradient-to-b from-orange-500 to-yellow-500 rounded-full mr-6 flex-shrink-0"></div>
            <div className="flex-1">
              <div className="flex items-center mb-6">
                <div className="p-2 bg-orange-100 rounded-xl mr-4">
                  <LuInfo className="w-6 h-6 text-orange-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">
                  {t('infoSn.section3_title')}
                </h2>
              </div>

              <div className="flex flex-col md:flex-row gap-8 items-center">
                <div className="flex-1">
                  <p className="text-lg text-gray-700 leading-relaxed mb-6">
                    {t('infoSn.section3_body')}
                  </p>
                </div>

                <div className="flex-shrink-0">
                  <div className="relative">
                    <div className="w-62 h-45 bg-gradient-to-br from-orange-500/20 to-yellow-500/20 rounded-full blur-xl absolute -inset-4 z-0"></div>
                    <div className="p-8 bg-white rounded-full border-2 border-orange-200 shadow-lg relative z-10">
                      <div className="flex items-center gap-2 text-center">
                        <div className="text-orange-600 font-bold text-lg">{t('infoSn.diagram_gross')}</div>
                        <div className="text-4xl pb-2 font-bold">-</div>
                        <div className="text-gray-600 font-medium">{t('infoSn.diagram_deductions')}</div>
                        <div className="text-4xl pb-2 font-bold">=</div>
                        <div className="text-orange-600 font-bold text-lg">{t('infoSn.diagram_net')}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Section déductions */}
        <motion.div 
          className="bg-white rounded-[2rem] p-8 md:p-10 mb-10 shadow-xl border border-orange-100"
          variants={itemVariants}
        >
          <div className="flex items-start">
            <div className="hidden md:block w-1.5 h-16 bg-gradient-to-b from-orange-500 to-yellow-500 rounded-full mr-6 flex-shrink-0"></div>
            <div className="flex-1">
              <div className="flex items-center mb-6">
                <div className="p-2 bg-orange-100 rounded-xl mr-4">
                  <LuCircleAlert className="w-6 h-6 text-orange-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">
                  {t('infoSn.section4_title')}
                </h2>
              </div>

              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                {t('infoSn.section4_intro')}
              </p>

              {/* IR */}
              <div className="mb-10">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                  <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-full flex items-center justify-center mr-4 shadow-md">
                    <span className="text-white font-bold">IR</span>
                  </div>
                  {t('infoSn.ir_heading')}
                </h3>

                <p className="text-gray-700 leading-relaxed mb-6">
                  {t('infoSn.ir_body')}
                </p>

                <div className="overflow-x-auto mb-8">
                  <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                    <thead>
                      <tr className="bg-gradient-to-r from-orange-500 to-yellow-500 text-white">
                        <th className="p-4 text-left font-semibold">{t('infoSn.table_income_header')}</th>
                        <th className="p-4 text-left font-semibold">{t('infoSn.table_rate_header')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr className="hover:bg-orange-50 transition-colors"><td className="p-4">0 - 630 000</td><td className="p-4 font-semibold">0%</td></tr>
                      <tr className="hover:bg-orange-50 transition-colors"><td className="p-4">630 001 - 1 500 000</td><td className="p-4 font-semibold">20%</td></tr>
                      <tr className="hover:bg-orange-50 transition-colors"><td className="p-4">1 500 001 - 4 000 000</td><td className="p-4 font-semibold">30%</td></tr>
                      <tr className="hover:bg-orange-50 transition-colors"><td className="p-4">4 000 001 - 8 000 000</td><td className="p-4 font-semibold">35%</td></tr>
                      <tr className="hover:bg-orange-50 transition-colors"><td className="p-4">8 000 001 - 13 500 000</td><td className="p-4 font-semibold">37%</td></tr>
                      <tr className="hover:bg-orange-50 transition-colors"><td className="p-4">13 500 001 - 50 000 000</td><td className="p-4 font-semibold">40%</td></tr>
                      <tr className="hover:bg-orange-50 transition-colors"><td className="p-4">50 000 001 et plus</td><td className="p-4 font-semibold">43%</td></tr>
                    </tbody>
                  </table>
                </div>

                <div className="p-6 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl border border-orange-200 shadow-sm">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    {t('infoSn.ir_reduction_intro')} <span className='text-orange-600 font-medium'>{t('infoSn.ir_article_173')}</span>{t('infoSn.ir_reduction_intro_end')}
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="bg-white p-4 rounded-xl border border-orange-200 shadow-sm">
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start">
                          <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span>{t('infoSn.parts_single_0')} <strong>{t('infoSn.parts_1')}</strong></span>
                        </li>
                        <li className="flex items-start">
                          <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span>{t('infoSn.parts_married_0')} <strong>{t('infoSn.parts_15')}</strong></span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-white p-4 rounded-xl border border-orange-200 shadow-sm">
                      <ul className="space-y-2 text-gray-700">
                        <li className="flex items-start">
                          <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span>{t('infoSn.parts_single_1')} <strong>{t('infoSn.parts_15')}</strong></span>
                        </li>
                        <li className="flex items-start">
                          <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span>{t('infoSn.parts_married_1')} <strong>{t('infoSn.parts_2')}</strong></span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-xl border border-orange-200 shadow-sm mb-4">
                    <div className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span className="text-gray-700">{t('infoSn.parts_child')} <strong>{t('infoSn.parts_05')}</strong></span>
                    </div>
                  </div>

                  <div className="p-4 bg-gradient-to-r from-orange-100 to-yellow-100 rounded-xl">
                    <p className="text-gray-700">
                      <span className='font-bold'>{t('infoSn.nb_label')}</span> {t('infoSn.ir_note')}
                      <span className='text-orange-600 font-medium'> {t('infoSn.ir_note_article')}</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* TRIMF */}
              <div className="mb-10">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                  <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-full flex items-center justify-center mr-4 shadow-md">
                    <span className="text-white font-bold">TF</span>
                  </div>
                  {t('infoSn.trimf_heading')}
                </h3>

                <div className="p-6 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl border border-orange-200 shadow-sm">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    {t('infoSn.trimf_body')}
                  </p>

                  <div className="p-4 bg-gradient-to-r from-orange-100 to-yellow-100 rounded-xl">
                    <p className="text-gray-700">
                      {t('infoSn.trimf_note')} <strong>{t('infoSn.trimf_amount')}</strong> {t('infoSn.trimf_note_end')}
                      <span className='text-orange-600 font-medium'> {t('infoSn.trimf_article')}</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Cotisations sociales */}
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                  <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-full flex items-center justify-center mr-4 shadow-md">
                    <span className="text-white font-bold">CS</span>
                  </div>
                  {t('infoSn.cs_heading')}
                </h3>

                <div className="p-6 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl border border-orange-200 shadow-sm">
                  <p className="text-gray-700 leading-relaxed mb-6">
                    {t('infoSn.cs_body')}
                  </p>

                  <div className="bg-white rounded-xl p-6 border border-orange-200 shadow-sm">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <div className="w-2 h-8 bg-orange-500 rounded-full mr-3"></div>
                      {t('infoSn.ipres_heading')}
                    </h4>

                    <div className="space-y-4">
                      <div className="flex items-start bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-lg border border-gray-200">
                        <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span className="text-gray-700">{t('infoSn.ipres_point1')}</span>
                      </div>

                      <div className="flex items-start bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-lg border border-gray-200">
                        <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span className="text-gray-700">{t('infoSn.ipres_point2_prefix')} <span className="font-bold text-orange-600">{t('infoSn.ipres_general_rate_label')}</span> {t('infoSn.ipres_point2_and')} <span className="font-bold text-orange-600">{t('infoSn.ipres_cadre_rate_label')}</span></span>
                      </div>

                      <div className="flex items-start bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-lg border border-gray-200">
                        <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                        <span className="text-gray-700">{t('infoSn.ipres_point3')}</span>
                      </div>
                    </div>

                    <div className="mt-6 p-5 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl border border-orange-200">
                      <p className="text-gray-700 font-semibold mb-4">{t('infoSn.ipres_ceilings_heading')}</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-white p-4 rounded-lg shadow-sm border border-orange-200 text-center">
                          <p className="text-gray-600 mb-2">{t('infoSn.ipres_general_label')}</p>
                          <p className="text-2xl font-bold text-orange-600">{t('infoSn.ipres_general_amount')}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow-sm border border-orange-200 text-center">
                          <p className="text-gray-600 mb-2">{t('infoSn.ipres_cadre_label')}</p>
                          <p className="text-2xl font-bold text-orange-600">{t('infoSn.ipres_cadre_amount')}</p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 p-4 bg-gradient-to-r from-orange-100 to-yellow-100 rounded-xl">
                      <p className="text-gray-700">
                        <span className='font-bold'>{t('infoSn.nb_label')}</span> {t('infoSn.ipres_note')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-gradient-to-r from-orange-100 to-yellow-100 rounded-xl shadow-sm border border-orange-200">
                <h4 className="text-lg font-semibold text-gray-900">
                  {t('infoSn.other_contributions')}
                </h4>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Footer avec conseil */}
        <motion.div 
          className="text-center mt-12"
          variants={itemVariants}
        >
          <div className="inline-block bg-gradient-to-r from-orange-500 to-yellow-500 rounded-xl p-6 shadow-lg">
            <div className="flex items-center justify-center space-x-3">
              <span className="text-2xl">💡</span>
              <p className="text-white font-semibold text-lg">
                {t('infoSn.footer_tip')}
              </p>
            </div>
          </div>

          <div className="mt-8 flex justify-center">
            <a
              href="/simulateur"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white rounded-xl shadow-md border border-orange-200 text-orange-600 font-medium hover:bg-orange-50 transition-colors"
            >
              {t('infoSn.footer_cta')}
              <LuArrowRight className="w-5 h-5" />
            </a>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default InfoSn;